import { Typography, useTheme } from '@mui/material';
import { computeAmount } from '../utils/decimal';
import { useMasking } from './MaskingProvider';

const MASK_MAP = {
  '1': 'I', '2': 'S', '3': 'E', '4': 'A', '5': 'Q',
  '6': 'G', '7': 'L', '8': 'B', '9': 'P', '0': 'X'
};

const maskValue = (str) =>
  String(str).replace(/[0-9]/g, (d) => MASK_MAP[d] || d);

/**
 * Resolve the value color per DESIGN_SYSTEM.md section 11. Pure function of the
 * theme palette so it is unit-testable without rendering.
 *
 * Variants:
 *   neutral          - text.primary at any sign, zero dim (#2207: Dashboard rows
 *                      that are not sums; red there means bad news only).
 *   row              - positive stays neutral (text.primary); only negatives draw color.
 *   subtotal / total - positive uses positive (emerald); negatives negative.
 *   hero             - positive uses positiveStrong ("balance" semantics); negatives negative.
 *   default (unset)  - legacy behavior (positive green) so existing callers are unchanged.
 *
 * Negative is always `negative`. Exactly zero is `text.dim` for v3 variants;
 * the legacy default keeps `text.secondary` to preserve the prior look.
 *
 * @param {object} palette  theme.palette
 * @param {string|undefined} variant
 * @param {boolean} isNegative
 * @param {boolean} isZero
 * @returns {string} a theme color
 */
export const resolveAmountColor = (palette, variant, isNegative, isZero) => {
  if (variant === 'neutral') return isZero ? palette.text.dim : palette.text.primary;
  if (isNegative) return palette.error.main;

  switch (variant) {
    case 'row':
      return isZero ? palette.text.dim : palette.text.primary;
    case 'subtotal':
    case 'total':
      return isZero ? palette.text.dim : palette.success.main;
    case 'hero':
      return isZero ? palette.text.dim : palette.positiveStrong;
    default:
      // Legacy: green positives, secondary zero.
      return isZero ? palette.text.secondary : palette.success.main;
  }
};

// Accepts either a v2 `amountCents` (signed int) or legacy v1 `credit`/`debit`
// dollar pair. New callers should pass amountCents.
//
// `variant` selects the section 11 coloring rule. Omit it to keep the legacy
// look (used by the un-migrated LedgerDetail/BudgetDetail line rows until Phase 5).
const AmountDisplay = ({ amountCents, credit, debit, variant }) => {
  const theme = useTheme();
  const { isMasked } = useMasking();
  const amount = typeof amountCents === 'number'
    ? computeAmount(amountCents > 0 ? amountCents / 100 : 0, amountCents < 0 ? -amountCents / 100 : 0)
    : computeAmount(credit, debit);
  const { formatted, isNegative, isZero } = amount;

  const color = resolveAmountColor(theme.palette, variant, isNegative, isZero);

  // v3 variants right-align always (section 1: "Numbers right-align, always").
  // The legacy default keeps its prior per-sign alignment so un-migrated callers
  // do not shift.
  const textAlign = variant
    ? 'right'
    : isNegative ? 'left' : isZero ? 'center' : 'right';

  return (
    <Typography
      component="span"
      fontWeight="medium"
      sx={{
        color,
        display: 'inline-block',
        width: '100%',
        textAlign,
      }}
    >
      {isMasked ? maskValue(formatted) : formatted}
    </Typography>
  );
};

export default AmountDisplay;
